/*
 * The live case at the top of the page: one report travelling through
 * the EU Directive's lifecycle (raised → sealed → acknowledged within
 * 7 days → follow-up → feedback within 3 months → closed), seen by the
 * reporter and by the case handler. The timeline is plain DOM and runs
 * without WebGL; the scene follows its phases when available.
 */
import { STATES, createCaseScene } from './scene';

const CASES = [
  {
    code: "DE",
    law: "HinSchG",
    category: "Procurement fraud",
    channel: "Web form",
    report: "The same manager approves and pays one supplier's invoices. Some have been paid twice.",
    question: "Thank you. Do you know roughly when this started?",
    answer: "Since March. I've attached two invoice numbers.",
    feedback: "An audit confirmed it. Approvals now need two people and the overpayment is being recovered."
  },
  {
    code: "FR",
    law: "Loi Waserman",
    category: "Data protection",
    channel: "Mobile",
    report: "Customer files are exported to a personal cloud drive every Friday.",
    question: "Is it one account or several?",
    answer: "One account, from the sales team.",
    feedback: "Access was revoked, the DPO was involved and exports to personal drives are now blocked."
  },
  {
    code: "ES",
    law: "Ley 2/2023",
    category: "Product safety",
    channel: "Voice message",
    report: "Batch 24-118 of the heaters failed the overheating test, but it's being shipped.",
    question: "Which warehouse holds the batch?",
    answer: "Getafe, aisle C.",
    feedback: "The batch was stopped before shipping and the test sign-off process was reviewed."
  },
  {
    code: "IT",
    law: "D.Lgs. 24/2023",
    category: "Environment",
    channel: "Web form",
    report: "Wash water from the plant is discharged at night, bypassing the filter.",
    question: "Have you seen this more than once?",
    answer: "Three times this month, around 2 a.m.",
    feedback: "The bypass valve was sealed and the competent authority was informed."
  },
  {
    code: "PT",
    law: "Lei 93/2021",
    category: "Money laundering",
    channel: "Mobile",
    report: "New corporate clients are onboarded without ID checks to hit monthly targets.",
    question: "Which team or branch is this?",
    answer: "The corporate desk at the Lisbon branch.",
    feedback: "The accounts were re-checked and onboarding is now blocked until ID is verified."
  },
  {
    code: "IE",
    law: "Protected Disclosures Act",
    category: "Food safety",
    channel: "Web form",
    report: "Cold-room temperature logs are filled in at the end of the week, not daily.",
    question: "Is the cold room working properly today?",
    answer: "It reached 9°C on Tuesday.",
    feedback: "The unit was repaired and temperatures are now logged automatically."
  },
  {
    code: "NL",
    law: "Wbk",
    category: "Tax evasion",
    channel: "Voice message",
    report: "Bonuses are being paid through a foreign entity to avoid payroll tax.",
    question: "Do you know which payment run?",
    answer: "The March run, booked as consulting fees.",
    feedback: "Finance reviewed it with external advisers and the payments were corrected."
  },
  {
    code: "PL",
    law: "Ustawa o ochronie sygnalistów",
    category: "Transport safety",
    channel: "Mobile",
    report: "Delivery drivers are told to skip their mandatory rest breaks on long routes.",
    question: "Is this a written instruction?",
    answer: "It's in the route plan messages.",
    feedback: "Route planning was changed and rest breaks are now enforced by the system."
  }
];

export function initCase() {
  const engine = document.querySelector(".case-engine");
  if (!engine) return;

  const $ = id => document.getElementById(id);

  const stateLabel = $("caseState");
  const substateLabel = $("caseSubstate");
  const thread = $("reporterThread");
  const trail = $("auditTrail");
  const dayLabel = $("caseDay");
  const ackMeter = $("ackMeter");
  const ackLabel = $("ackLabel");
  const feedbackMeter = $("feedbackMeter");
  const feedbackLabel = $("feedbackLabel");
  const handledLabel = $("handledCount");
  const journeySteps = [...engine.querySelectorAll(".journey-step")];
  const journeyProgress = $("journeyProgress");
  const pickers = [...engine.querySelectorAll("[data-origin]")];
  const field = name => engine.querySelector(`[data-case="${name}"]`);

  let scene = null;
  try {
    scene = createCaseScene($("caseCanvas"), engine.querySelector(".scene-labels"));
  } catch (error) {
    console.warn("Case scene unavailable.", error);
  }
  if (!scene) engine.classList.add("no-webgl");

  /*
   * Waits count only while the case is on screen, so a visitor who
   * scrolls back finds the story where they left it.
   */
  let onScreen = true;
  if ("IntersectionObserver" in window) {
    new IntersectionObserver(entries => {
      onScreen = entries[0].isIntersecting;
    }, { rootMargin: "80px 0px" }).observe(engine);
  }

  const waits = [];
  let last = performance.now();
  (function tick(now) {
    const dt = Math.min(now - last, 100);
    last = now;
    if (onScreen && !document.hidden) {
      for (let i = waits.length - 1; i >= 0; i--) {
        waits[i].left -= dt;
        if (waits[i].left <= 0) waits.splice(i, 1)[0].resolve();
      }
    }
    requestAnimationFrame(tick);
  })(last);

  const wait = ms => new Promise(resolve => waits.push({ left: ms, resolve }));

  const call = (method, ...args) => scene && scene[method](...args);

  const randomInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));
  const randomCode = length => Array.from({ length }, () => "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"[randomInt(0, 31)]).join("");

  function setState(label, sub) {
    stateLabel.textContent = label;
    substateLabel.textContent = sub;
  }

  function setJourney(index, finished = false) {
    journeySteps.forEach((step, i) => {
      step.classList.toggle("active", i === index && !finished);
      step.classList.toggle("done", i < index || (finished && i <= index));
    });
    journeyProgress.style.width = Math.max(0, index) / (journeySteps.length - 1) * 100 + "%";
  }

  let day = 0;
  function setDay(value) {
    day = value;
    dayLabel.textContent = value;
  }

  // Count the case clock up to a day, in step with the scene
  function countTo(target, ms) {
    const from = day;
    const start = performance.now();
    (function step(now) {
      const t = Math.min(1, (now - start) / ms);
      setDay(Math.round(from + (target - from) * t));
      if (t < 1) requestAnimationFrame(step);
    })(start);
  }

  function say(side, text, meta) {
    const el = document.createElement("div");
    el.className = `msg ${side}`;
    el.innerHTML = `<div class="bubble"></div><span class="msg-meta">${meta}</span>`;
    el.querySelector(".bubble").textContent = text;
    thread.append(el);
  }

  function note(html) {
    const el = document.createElement("div");
    el.className = "msg note";
    el.innerHTML = html;
    thread.append(el);
  }

  function log(tag, text, when) {
    const item = document.createElement("li");
    item.innerHTML = `<span class="log-tag ${tag}">${tag}</span><span>${text}</span><em>${when}</em>`;
    trail.prepend(item);
    while (trail.children.length > 7) trail.lastElementChild.remove();
  }

  let run = 0;
  let current = 0;
  let handled = 1284;
  const CANCELLED = Symbol("cancelled");

  async function play(index) {
    const id = ++run;
    const step = async ms => {
      await wait(ms);
      if (id !== run) throw CANCELLED;
    };

    const c = CASES[index];
    const origin = STATES.findIndex(([code]) => code === c.code);
    const city = STATES[origin][1];
    const ack = randomInt(1, 2);
    const asked = ack + randomInt(3, 8);
    const sent = randomInt(24, 61);
    const caseId = `WB-${new Date().getFullYear()}-${randomInt(1000, 9999)}`;
    const reporter = `${randomCode(4)}-${randomCode(4)}`;

    current = index;
    pickers.forEach(button => button.setAttribute("aria-pressed", String(button.dataset.origin === c.code)));

    thread.replaceChildren();
    trail.replaceChildren();
    $("caseId").textContent = caseId;
    field("origin").textContent = `${city}, ${c.code}`;
    field("law").textContent = c.law;
    field("category").textContent = c.category;
    field("channel").textContent = c.channel;
    field("reporter").textContent = "Anonymous";
    setDay(0);
    // Empty the deadline bars at once, not with their fill transition
    [ackMeter, feedbackMeter].forEach(meter => {
      meter.style.transition = "none";
      meter.style.width = "0%";
      meter.offsetWidth;
      meter.style.transition = "";
    });
    ackLabel.textContent = "Due by day 7";
    feedbackLabel.textContent = "Due within 3 months";
    engine.classList.remove("closed");

    try {
      setJourney(0);
      setState("REPORT RAISED", `${city.toUpperCase()} · ${c.channel.toUpperCase()}`);
      call("raise", origin);
      say("out", c.report, `You · ${c.channel.toLowerCase()} · day 0`);
      log("received", `${c.category} report via ${c.channel.toLowerCase()}`, "Day 0");
      await step(1600);

      setJourney(1);
      setState("IDENTITY SEALED", "ANONYMOUS · ENCRYPTED");
      call("shield");
      note(`Your case code is <b>${reporter}</b>. Use it to follow your case, without an account.`);
      log("sealed", "Identity sealed · case code issued", "Day 0");
      await step(1400);

      setState("SECURE CHANNEL", `${city.toUpperCase()} → YOUR INBOX`);
      call("send");
      log("routed", "Delivered to the designated case handler", "Day 0");
      await step(1800);

      setJourney(2);
      setState("ACKNOWLEDGED", `DAY ${ack} · DEADLINE DAY 7`);
      call("ack");
      countTo(ack, 600);
      ackMeter.style.width = `${ack / 7 * 100}%`;
      ackLabel.textContent = `Done on day ${ack} of 7`;
      say("in", "We've received your report. You'll hear from us within 3 months.", `Case handler · day ${ack}`);
      log("ack", "Receipt acknowledged", `Day ${ack}`);
      await step(1700);

      setJourney(3);
      setState("FOLLOW-UP", "TWO-WAY · STILL ANONYMOUS");
      call("message", true);
      countTo(asked, 700);
      say("in", c.question, `Case handler · day ${asked}`);
      log("follow-up", "Question sent through the case code", `Day ${asked}`);
      await step(1500);

      call("message", false);
      countTo(asked + 1, 400);
      say("out", c.answer, `You · day ${asked + 1}`);
      log("reply", "Reporter answered, still anonymous", `Day ${asked + 1}`);
      await step(1500);

      setJourney(4);
      setState("FEEDBACK GIVEN", `DAY ${sent} · DEADLINE 3 MONTHS`);
      call("feedback");
      countTo(sent, 900);
      feedbackMeter.style.width = `${sent / 90 * 100}%`;
      feedbackLabel.textContent = `Done on day ${sent}`;
      say("in", c.feedback, `Case handler · day ${sent}`);
      log("feedback", "Outcome shared with the reporter", `Day ${sent}`);
      await step(1900);

      setJourney(4, true);
      setState("CASE CLOSED", "EVERY DEADLINE MET · RECORD KEPT");
      engine.classList.add("closed");
      call("close");
      handled += 1;
      handledLabel.textContent = handled.toLocaleString("en-US");
      log("closed", "Case closed · record archived", `Day ${sent}`);
      await step(2600);

      play((current + 1) % CASES.length);
    } catch (error) {
      if (error !== CANCELLED) throw error;
    }
  }

  pickers.forEach(button => {
    button.addEventListener("click", () => {
      play(CASES.findIndex(c => c.code === button.dataset.origin));
    });
  });

  $("caseReplay").addEventListener("click", () => play((current + 1) % CASES.length));

  play(0);
}
